package link.erpango.quip.features.transfer

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.entities.AcceptTransferResponse
import link.erpango.quip.domain.entities.CancelTransferResponse
import link.erpango.quip.domain.entities.PendingTransferItem
import link.erpango.quip.domain.entities.QRData
import link.erpango.quip.domain.entities.TransferQRResponse
import link.erpango.quip.domain.entities.TransferStatusResponse
import link.erpango.quip.domain.usecases.IGarageUseCase

sealed class TransferState {
    object Idle : TransferState()
    object Loading : TransferState()
    data class Success(val message: String) : TransferState()
    data class Error(val error: String) : TransferState()
    data class QrGenerated(
        val token: String,
        /** `qr_data` tal cual lo devuelve el servidor: es lo que se codifica en el QR. */
        val qrData: QRData,
        /** ms desde epoch. */
        val expiresAt: Long,
        val vehicleVin: String,
        /** OTP corto (`ABCD-EFGH`) para introducir a mano; null con backends antiguos. */
        val otpCode: String? = null,
        /** Solo el nombre de pila del destinatario. */
        val targetUserFirstName: String? = null,
        /** true si es una transferencia que ya estaba activa (reanudada o 409). */
        val alreadyActive: Boolean = false
    ) : TransferState()
    /** La transferencia del emisor ha sido cancelada (por él o detectado al consultar el estado). */
    data class Cancelled(val message: String) : TransferState()
    /** El servidor la marca como `expired` o ya ha pasado `expires_at`. */
    object Expired : TransferState()
}

sealed class RecipientState {
    object Idle : RecipientState()
    data class Loading(val userId: Int) : RecipientState()
    data class Found(val userId: Int, val firstName: String) : RecipientState()
    data class Error(val userId: Int?, val message: String) : RecipientState()
}

/** Construye el estado de QR a partir de la respuesta del servidor (POST, 409 o GET activa). */
internal fun TransferQRResponse.toQrGenerated(fallbackFirstName: String? = null): TransferState.QrGenerated =
    TransferState.QrGenerated(
        token = token,
        qrData = qrData,
        expiresAt = expiresAt,
        vehicleVin = vehicleVin,
        otpCode = otpCode ?: qrData.otpCode,
        targetUserFirstName = targetUserFirstName ?: fallbackFirstName,
        alreadyActive = alreadyActive
    )

sealed class PendingTransferEvent {
    object Idle : PendingTransferEvent()
    object Loading : PendingTransferEvent()
    data class Loaded(val transfers: List<PendingTransferItem>) : PendingTransferEvent()
    data class Error(val error: String) : PendingTransferEvent()
}

class TransferViewModel(
    private val mainInteractor: IGarageUseCase,
    private val vehicleVin: String? = null
) : ViewModel() {
    
    private val _transferState = MutableStateFlow<TransferState>(TransferState.Idle)
    val transferState = _transferState.asStateFlow()
    
    private val _pendingTransfers = MutableStateFlow<PendingTransferEvent>(PendingTransferEvent.Idle)
    val pendingTransfers = _pendingTransfers.asStateFlow()

    private val _recipientState = MutableStateFlow<RecipientState>(RecipientState.Idle)
    val recipientState = _recipientState.asStateFlow()

    /**
     * Al abrir la pantalla del emisor: si el vehículo ya tiene una transferencia pendiente se
     * muestra directamente (QR/OTP + cancelar); si no (404) se deja el formulario.
     */
    fun loadActiveTransfer() {
        val vin = vehicleVin ?: return
        viewModelScope.launch {
            _transferState.value = TransferState.Loading
            _recipientState.value = RecipientState.Idle
            _transferState.value = try {
                when (val response = mainInteractor.getActiveQrTransfer(vin)) {
                    is DResponse.Success -> response.data?.toQrGenerated() ?: TransferState.Idle
                    is DResponse.Error -> TransferState.Error(response.error ?: "Error desconocido")
                    else -> TransferState.Idle
                }
            } catch (e: Exception) {
                TransferState.Error(e.message ?: "Error de red")
            }
        }
    }

    /** Resuelve el nombre de pila del destinatario antes de permitir generar la transferencia. */
    fun lookupRecipient(userId: Int) {
        if (userId <= 0) {
            _recipientState.value = RecipientState.Error(userId, "El ID debe ser mayor que 0")
            return
        }
        viewModelScope.launch {
            _recipientState.value = RecipientState.Loading(userId)
            val result = try {
                when (val response = mainInteractor.getTransferRecipient(userId)) {
                    is DResponse.Success -> RecipientState.Found(userId, response.data.firstName)
                    is DResponse.Error -> RecipientState.Error(userId, response.error ?: "No se ha podido comprobar el usuario")
                    else -> RecipientState.Error(userId, "Respuesta inesperada del servidor")
                }
            } catch (e: Exception) {
                RecipientState.Error(userId, e.message ?: "Error de red")
            }
            // Si el usuario ha seguido escribiendo, descarta la respuesta de un ID anterior.
            val current = _recipientState.value
            if (current is RecipientState.Loading && current.userId == userId) {
                _recipientState.value = result
            }
        }
    }

    fun clearRecipient() {
        _recipientState.value = RecipientState.Idle
    }

    /**
     * Aplica una respuesta de estado obtenida por polling. Devuelve true si el estado es
     * terminal y hay que dejar de consultar ("accepted" lo gestiona la pantalla).
     */
    fun onStatusPolled(status: TransferStatusResponse): Boolean {
        val current = _transferState.value as? TransferState.QrGenerated ?: return true
        return when {
            status.isAccepted() -> true
            status.isCancelled() -> {
                _transferState.value = TransferState.Cancelled(status.message ?: "La transferencia ha sido cancelada")
                true
            }
            status.isExpired() -> {
                _transferState.value = TransferState.Expired
                true
            }
            else -> {
                status.expiresAt?.let { serverExpiry ->
                    if (serverExpiry != current.expiresAt) {
                        _transferState.value = current.copy(expiresAt = serverExpiry)
                    }
                }
                false
            }
        }
    }

    fun markExpired() {
        if (_transferState.value is TransferState.QrGenerated) {
            _transferState.value = TransferState.Expired
        }
    }

    // QR Transfer initiation
    fun initiateQrTransfer(targetUserId: Int, expiryHours: Int = 24) {
        viewModelScope.launch {
            _transferState.value = TransferState.Loading
            try {
                val vin = vehicleVin ?: run {
                    _transferState.value = TransferState.Error("No se proporcionó VIN del vehículo")
                    return@launch
                }
                
                if (targetUserId <= 0) {
                    _transferState.value = TransferState.Error("El ID de usuario debe ser mayor que 0")
                    return@launch
                }
                
                val response = mainInteractor.initiateQrTransfer(vin, targetUserId, expiryHours)
                when (response) {
                    is DResponse.Success -> {
                        val knownName = (_recipientState.value as? RecipientState.Found)
                            ?.takeIf { it.userId == targetUserId }?.firstName
                        _transferState.value = response.data.toQrGenerated(fallbackFirstName = knownName)
                    }
                    is DResponse.Error -> {
                        _transferState.value = TransferState.Error(response.error ?: "Error desconocido")
                    }
                    else -> {
                        _transferState.value = TransferState.Error("Respuesta inesperada del servidor")
                    }
                }
            } catch (e: Exception) {
                _transferState.value = TransferState.Error(e.message ?: "Error de red")
            }
        }
    }

    // Direct transfer initiation
    fun initiateDirectTransfer(targetUserId: Int, isPrimary: Boolean = true) {
        viewModelScope.launch {
            _transferState.value = TransferState.Loading
            try {
                val vin = vehicleVin ?: run {
                    _transferState.value = TransferState.Error("No se proporcionó VIN del vehículo")
                    return@launch
                }
                
                val response = mainInteractor.initiateDirectTransfer(vin, targetUserId, isPrimary)
                when (response) {
                    is DResponse.Success -> {
                        _transferState.value = TransferState.Success(response.data.message)
                    }
                    is DResponse.Error -> {
                        _transferState.value = TransferState.Error(response.error ?: "Error desconocido")
                    }
                    else -> {
                        _transferState.value = TransferState.Error("Respuesta inesperada del servidor")
                    }
                }
            } catch (e: Exception) {
                _transferState.value = TransferState.Error(e.message ?: "Error de red")
            }
        }
    }

    // Accept transfer via token
    fun acceptTransfer(rawCode: String) {
        viewModelScope.launch {
            // Acepta token del QR, OTP (normalizado) o el JSON del QR pegado a mano.
            val token = TransferCode.resolveManualInput(rawCode).orEmpty()
            if (token.isBlank()) {
                _transferState.value = TransferState.Error("Token inválido")
                return@launch
            }
            
            _transferState.value = TransferState.Loading
            try {
                val response = mainInteractor.acceptTransfer(token)
                when (response) {
                    is DResponse.Success -> {
                        _transferState.value = TransferState.Success(response.data.message)
                    }
                    is DResponse.Error -> {
                        _transferState.value = TransferState.Error(response.error ?: "Error desconocido")
                    }
                    else -> {
                        _transferState.value = TransferState.Error("Respuesta inesperada del servidor")
                    }
                }
            } catch (e: Exception) {
                _transferState.value = TransferState.Error(e.message ?: "Error de red")
            }
        }
    }

    // Cancel transfer via token
    fun cancelTransfer(rawCode: String) {
        viewModelScope.launch {
            val token = TransferCode.resolveManualInput(rawCode).orEmpty()
            if (token.isBlank()) {
                _transferState.value = TransferState.Error("Token inválido")
                return@launch
            }
            
            _transferState.value = TransferState.Loading
            try {
                val response = mainInteractor.cancelTransfer(token)
                when (response) {
                    is DResponse.Success -> {
                        _transferState.value = TransferState.Cancelled(response.data.message)
                    }
                    is DResponse.Error -> {
                        _transferState.value = TransferState.Error(response.error ?: "Error desconocido")
                    }
                    else -> {
                        _transferState.value = TransferState.Error("Respuesta inesperada del servidor")
                    }
                }
            } catch (e: Exception) {
                _transferState.value = TransferState.Error(e.message ?: "Error de red")
            }
        }
    }

    // Load pending transfers
    fun loadPendingTransfers() {
        viewModelScope.launch {
            _pendingTransfers.value = PendingTransferEvent.Loading
            try {
                val response = mainInteractor.getPendingTransfers()
                when (response) {
                    is DResponse.Success -> {
                        _pendingTransfers.value = PendingTransferEvent.Loaded(response.data)
                    }
                    is DResponse.Error -> {
                        _pendingTransfers.value = PendingTransferEvent.Error(response.error ?: "Error desconocido")
                    }
                    else -> {
                        _pendingTransfers.value = PendingTransferEvent.Error("Respuesta inesperada del servidor")
                    }
                }
            } catch (e: Exception) {
                _pendingTransfers.value = PendingTransferEvent.Error(e.message ?: "Error de red")
            }
        }
    }

    // Check transfer status - used for polling by sender screen
    suspend fun checkTransferStatus(token: String): DResponse<TransferStatusResponse> {
        return mainInteractor.checkTransferStatus(token)
    }

    fun resetState() {
        _transferState.value = TransferState.Idle
    }
    
    fun showError(message: String) {
        _transferState.value = TransferState.Error(message)
    }
}
